import { useTranslation } from 'react-i18next';
import { homeEvents } from '@/mocks/home';

export default function Events() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  return (
    <section id="events" className="relative bg-background-900 overflow-hidden">
      <div className="absolute inset-0 tactical-grid opacity-10 pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 lg:py-32">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="section-label mb-4">
            {isEn ? 'Calendar' : isRu ? 'Календарь' : 'Күнтізбе'}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 uppercase tracking-tight">
            {t('events.title', isEn ? 'Upcoming Events' : isRu ? 'Предстоящие мероприятия' : 'Алдағы іс-шаралар')}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-foreground-500 text-sm leading-relaxed">
            {isEn
              ? 'Organizing sports events is vital for fostering athletic community and military applied sports excellence'
              : isRu
              ? 'Организация мероприятий является важной частью деятельности военно-прикладных видов спорта'
              : 'Іс-шараларды ұйымдастыру — әскери-қолданбалы спорт түрлерін дамыту мен қатысушыларды қолдаудың маңызды бөлігі'}
          </p>
        </div>

        {/* Events */}
        <div className="space-y-6 md:space-y-8 max-w-5xl mx-auto">
          {homeEvents.map((event, index) => {
            const title = isEn ? event.titleEn : isRu ? event.titleRu : event.titleKk;
            const location = isEn ? event.locationEn : isRu ? event.locationRu : event.locationKk;
            const description = isEn ? event.descriptionEn : isRu ? event.descriptionRu : event.descriptionKk;
            const guests = isEn ? event.guestsEn : isRu ? event.guestsRu : event.guestsKk;
            const equipment = isEn ? event.equipmentEn : isRu ? event.equipmentRu : event.equipmentKk;

            return (
              <div
                key={event.id}
                className="group relative bg-background-800/30 border border-background-700/40 overflow-hidden hover:border-accent-500/30 transition-all duration-500 rounded-lg"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="relative w-full md:w-72 lg:w-80 h-48 md:h-auto flex-shrink-0 overflow-hidden bg-background-950">
                    <img
                      src={event.image}
                      alt={title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          'https://readdy.ai/api/search-image?query=UAE%20Dubai%20outdoor%20competition%20arena%20international%20SWAT%20Challenge%20championship%20Kazakh%20East%20Asian%20Mongoloid%20young%20men%20athletes%20broad%20cheekbones%20almond%20eyes%20in%20military%20camouflage%20uniforms%20competing%20at%20finish%20line%20celebrating%20victory%20cinematic%20wide%20dramatic%20warm%20golden%20amber%20light%20dust%20haze%20premium%20sports%20photography%20no%20text%20no%20western%20caucasian%20faces&width=800&height=500&seq=event-kz-swat-v4&orientation=landscape&nocache=true';
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background-800/80 hidden md:block" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-800/80 to-transparent md:hidden" />

                    {/* Event index */}
                    <div className="absolute top-4 left-4 font-mono text-xs text-accent-500/70">
                      EVT-{String(index + 1).padStart(2, '0')}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-5 md:p-6 lg:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-accent-500 text-xs font-mono">
                        <i className="ri-calendar-line" />
                        {event.date}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-foreground-600 text-xs font-mono">
                        <i className="ri-map-pin-line" />
                        {location}
                      </span>
                    </div>

                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-100 uppercase tracking-wide mb-3">
                      {title}
                    </h3>

                    <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                      {description}
                    </p>

                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-foreground-600 font-mono">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-user-line" />
                        {guests}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-shield-line" />
                        {equipment}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom accent */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-accent-500 group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-accent-500/20 to-transparent" />
    </section>
  );
}